import { motion, useInView } from 'framer-motion'
import { useMemo, useRef } from 'react'
import { DONE_STATUS, type Client, type ContentItem } from '../data/types'
import { ProgressBar } from './ProgressBar'
import { cn } from '../lib/cn'
import type { Requirement } from '../context/AppDataContext'
import { isContentRequirement } from './Requirements'
import { serviceOf } from '../lib/services'

interface ContentSummaryProps {
  content: ContentItem[]
  clientId: string
  color: string
  /**
   * What is owed this month. When there is any, every tile counts against it
   * — "Reels 3 / 4" is three posted of the four promised — rather than
   * against whichever pieces happen to exist, which read 1 / 3 for a client
   * owed four and hid everything nobody had started.
   */
  requirements?: Requirement[]
}

interface ServiceTile {
  label: string
  posted: number
  target: number
}

export function ContentSummary({ content, clientId, color, requirements = [] }: ContentSummaryProps) {
  const stats = useMemo(() => {
    const items = clientId === 'all' ? content : content.filter((c) => c.clientId === clientId)
    const owed = requirements.filter((r) => (clientId === 'all' || r.client_id === clientId) && isContentRequirement(r))
    const approved = items.filter((c) => c.approvalStatus === 'Approved').length

    if (owed.length > 0) {
      const tiles = new Map<string, ServiceTile>()
      for (const r of owed) {
        const label = serviceOf(r)
        const tile = tiles.get(label) ?? { label, posted: 0, target: 0 }
        tile.posted += Math.min(r.posted, r.target_count)
        tile.target += r.target_count
        tiles.set(label, tile)
      }
      const target = owed.reduce((n, r) => n + r.target_count, 0)
      const posted = owed.reduce((n, r) => n + Math.min(r.posted, r.target_count), 0)
      const planned = owed.reduce((n, r) => n + Math.min(r.planned, r.target_count), 0)
      return {
        total: planned,
        totalOf: target as number | undefined,
        services: [...tiles.values()],
        pending: target - posted,
        pendingOf: target,
        approved,
        approvedOf: items.length,
        overallPct: target ? Math.round((posted / target) * 100) : 0,
      }
    }

    // Nothing owed: count the pieces that are there, by format.
    const done = (type: ContentItem['contentType']) => items.filter((c) => c.contentType === type)
    const services: ServiceTile[] = (['Reel', 'Static Post', 'Carousel', 'Story'] as ContentItem['contentType'][])
      .map((type) => ({ type, rows: done(type) }))
      .filter((x) => x.rows.length > 0)
      .map((x) => ({ label: x.type === 'Static Post' ? 'Posts' : `${x.type}s`, posted: x.rows.filter((c) => c.status === DONE_STATUS).length, target: x.rows.length }))
    const completed = items.filter((c) => c.status === DONE_STATUS).length
    return {
      total: items.length,
      totalOf: undefined as number | undefined,
      services,
      pending: items.length - completed,
      pendingOf: items.length,
      approved,
      approvedOf: items.length,
      overallPct: items.length ? Math.round((completed / items.length) * 100) : 0,
    }
  }, [content, clientId, requirements])

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-card border border-shell-border bg-shell p-5 shadow-card">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {stats.totalOf === undefined ? <Tile label="Total Planned" value={stats.total} /> : <Tile label="Total Planned" value={stats.total} total={stats.totalOf} color={color || "currentColor"} />}
        {stats.services.map((s) => (
          <Tile key={s.label} label={s.label} value={s.posted} total={s.target} color={color || "currentColor"} />
        ))}
        <Tile label="Pending" value={stats.pending} total={stats.pendingOf} color="#d98e1c" valueClassName="text-warning-500" />
        <Tile label="Approved" value={stats.approved} total={stats.approvedOf} color="#2f9e57" valueClassName="text-success-500" />
      </div>
      <div className="mt-3 rounded-2xl bg-shell-border/40 px-5 py-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-shell-text">Overall completion</span>
          <span className="font-display text-lg font-bold text-shell-text-active">{stats.overallPct}%</span>
        </div>
        <div className="mt-3">
          <WaveMeter value={stats.overallPct} />
        </div>
      </div>
    </motion.div>
  )
}

function Tile({
  label,
  value,
  total,
  color,
  valueClassName,
}: {
  label: string
  value: number
  total?: number
  color?: string
  valueClassName?: string
}) {
  const hasMeter = total !== undefined
  const pct = hasMeter && total > 0 ? (value / total) * 100 : 0
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-shell-border/40 px-4 py-3.5">
      <div className="min-w-0">
        <p className={cn('font-display text-2xl font-bold', valueClassName ?? 'text-shell-text-active')}>
          {value}
          {hasMeter && <span className="text-shell-text/50"> / {total}</span>}
        </p>
        <p className="mt-0.5 truncate text-xs font-medium text-shell-text">{label}</p>
      </div>
      {hasMeter && (
        <div className="shrink-0">
          <SegmentedMeter value={pct} color={color as any} segments={10} barHeight={20} />
        </div>
      )}
    </div>
  )
}

/** A row of small filled/unfilled bars — like the battery-level indicator
 * in the reference dashboard, rather than one continuous fill — with each
 * filled bar lighting up in sequence on mount instead of a single smooth
 * sweep. */
function SegmentedMeter({
  value,
  color = '#FC2265',
  segments = 12,
  barHeight = 18,
}: {
  value: number
  color?: string
  segments?: number
  barHeight?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -40px 0px' })
  const clamped = Math.max(0, Math.min(100, value))
  const filled = Math.round((clamped / 100) * segments)

  return (
    <div ref={ref} className="flex items-end gap-[3px]">
      {Array.from({ length: segments }).map((_, i) => (
        <motion.span
          key={i}
          className="w-[3px] rounded-full"
          style={{ height: barHeight, backgroundColor: i < filled ? color : 'var(--color-shell-border)' }}
          initial={{ opacity: 0, scaleY: 0.3 }}
          animate={inView ? { opacity: 1, scaleY: 1 } : {}}
          transition={{ delay: i < filled ? i * 0.02 : 0, type: 'spring', stiffness: 420, damping: 26 }}
        />
      ))}
    </div>
  )
}

/** A horizontal loader that fills left-to-right with an animated liquid
 * wave at its leading edge, rather than a flat fill line — the wave
 * pattern is twice the container's width and scrolls left on a loop; since
 * it's drawn as two identical periods, translating it exactly one period
 * (50% of its own width) loops seamlessly. */
function WaveMeter({ value, color = '#c1602f', height = 34 }: { value: number; color?: string; height?: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -40px 0px' })
  const clamped = Math.max(0, Math.min(100, value))
  const pct = inView ? clamped : 0

  return (
    <div ref={ref} className="relative w-full overflow-hidden rounded-full bg-shell-border/40" style={{ height }}>
      <motion.div
        className="absolute inset-y-0 left-0 overflow-hidden"
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="absolute inset-y-0 left-0 h-full w-[200%] animate-wave-scroll">
          <svg viewBox="0 0 200 40" preserveAspectRatio="none" className="block h-full w-full">
            <path
              d="M0,12 Q25,4 50,12 T100,12 L100,40 L0,40 Z M100,12 Q125,4 150,12 T200,12 L200,40 L100,40 Z"
              fill={color}
            />
          </svg>
        </div>
      </motion.div>
    </div>
  )
}

export function getClientColorSafe(clientId: string, clients: Client[]) {
  if (clientId === 'all') return '#FC2265'
  return clients.find((c) => c.id === clientId)?.color ?? '#FC2265'
}
